import { useState } from 'react';
import { Search, UserPlus } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { PasswordInput } from '../../ui/password-input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../../ui/dialog';
import { Checkbox } from '../../ui/checkbox';
import { Badge } from '../../ui/badge';
import { RegisterUserDialogProps } from './userTypes';

export function RegisterUserDialog({
  open,
  onOpenChange,
  name,
  setName,
  username,
  setUsername,
  email,
  setEmail,
  password,
  setPassword,
  roles,
  toggleRole,
  roleCatalog,
  error,
  success,
  loading,
  onSubmit
}: RegisterUserDialogProps) {
  const [roleSearchQuery, setRoleSearchQuery] = useState('');

  // Filter roles based on search
  const filteredRoles = roleCatalog.filter((role) =>
    role.label.toLowerCase().includes(roleSearchQuery.toLowerCase()) ||
    role.description.toLowerCase().includes(roleSearchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="flex-1 sm:flex-initial">
          <UserPlus className="mr-2 h-4 w-4" />
          <span className="hidden sm:inline">Novo usuario</span>
          <span className="sm:hidden">Novo</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cadastrar usuario</DialogTitle>
          <DialogDescription>
            Crie credenciais para um novo colaborador e atribua os papeis necessarios.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={onSubmit}>
          <div className="space-y-2">
            <Label htmlFor="register-name">Nome completo</Label>
            <Input
              id="register-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="register-username">Username</Label>
            <Input
              id="register-username"
              type="text"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="usuario.exemplo"
              pattern="[a-zA-Z0-9_-]+"
              title="Apenas letras, numeros, underscore e hifen sao permitidos"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="register-email">E-mail corporativo</Label>
            <Input
              id="register-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="register-password">Senha temporaria</Label>
            <PasswordInput
              id="register-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={8}
            />
          </div>

          <div className="space-y-2">
            <Label>Papeis de acesso</Label>

            {/* Selected roles display */}
            {roles.length > 0 && (
              <div className="flex flex-wrap gap-1 p-2 rounded-md border border-input bg-muted/50">
                {roles.map((roleId) => {
                  const role = roleCatalog.find((r) => r.id === roleId);
                  return (
                    <Badge key={roleId} variant="secondary" className="text-xs">
                      {role?.label || roleId}
                    </Badge>
                  );
                })}
              </div>
            )}

            {/* Search field */}
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar papeis..."
                value={roleSearchQuery}
                onChange={(e) => setRoleSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Scrollable checkbox list */}
            <div className="max-h-[160px] overflow-y-auto rounded-md border border-input">
              {filteredRoles.length > 0 ? (
                <div className="p-0.5">
                  {filteredRoles.map((role) => (
                    <label
                      key={role.id}
                      className="flex items-center gap-2 rounded px-2 py-1 cursor-pointer hover:bg-accent transition-colors"
                    >
                      <Checkbox
                        checked={roles.includes(role.id)}
                        onCheckedChange={() => toggleRole(role.id)}
                      />
                      <span className="text-sm flex-1 truncate">{role.label}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="p-3 text-center text-sm text-muted-foreground">
                  Nenhum papel encontrado
                </div>
              )}
            </div>
          </div>

          {error && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          {success && (
            <p className="rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm text-primary">
              {success}
            </p>
          )}

          <DialogFooter>
            <Button
              type="submit"
              disabled={loading || roles.length === 0}
            >
              {loading ? 'Salvando...' : 'Criar usuario'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
